/**
 * Vista tecnica di un digest: **cosa è stato mandato al modello e cosa ha
 * risposto**, senza interpretazioni (F-108).
 *
 * Serve alla domanda che la pagina normale non può affrontare: quando un
 * giudizio sorprende, la spiegazione sta quasi sempre nell'input — un titolo
 * che non è entrato, una posizione descritta male, una notizia che ha spostato
 * il tono. Leggere solo l'output porta a diagnosticare il modello quando il
 * problema è nei dati.
 *
 * Due scelte:
 *
 *  1. **Testo grezzo, nessuna formattazione.** È la vista in cui si cerca una
 *     parola con Ctrl+F e si confronta con quello che ci si aspettava. Renderlo
 *     grazioso lo renderebbe meno utile.
 *  2. **Un digest anteriore a questa versione lo dichiara.** La diagnostica si
 *     registra da adesso: un riquadro vuoto sembrerebbe un guasto, mentre è una
 *     data di nascita.
 */

import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ApiError } from '@/lib/api-client';
import { formatDateTime } from '@/lib/format';
import { Alert, Badge, Card, Spinner, TableWrap } from '@/ui';
import { useBriefingDebug } from './api';
import './briefing.css';

const EMPTY = '—';

function RawBlock({ title, value }: { title: string; value: string | null }) {
  return (
    <Card title={title} subtitle={value ? `${value.length.toLocaleString('it-IT')} caratteri` : undefined}>
      {value ? (
        <pre className="briefing-debug__raw">{value}</pre>
      ) : (
        <p className="cell--minor">
          Non registrato: questo digest è stato generato prima che la diagnostica venisse conservata.
        </p>
      )}
    </Card>
  );
}

export function BriefingDebugPage() {
  const { date } = useParams<{ date: string }>();
  const [searchParams] = useSearchParams();
  const revisionParam = Number.parseInt(searchParams.get('revision') ?? '', 10);
  const revision = Number.isFinite(revisionParam) ? revisionParam : null;

  const query = useBriefingDebug(date ?? null, revision);

  if (query.isPending) return <Spinner label="Caricamento della diagnostica…" />;

  if (query.isError) {
    const notFound = query.error instanceof ApiError && query.error.status === 404;
    return (
      <Alert variant="error" title={notFound ? 'Digest non trovato' : 'Errore'}>
        {notFound
          ? 'Non esiste un digest per questa data (o per questa revisione).'
          : query.error instanceof Error
            ? query.error.message
            : 'Errore imprevisto'}
      </Alert>
    );
  }

  const debug = query.data;

  return (
    <div className="briefing-debug">
      <header className="page-header">
        <div>
          <h1>Diagnostica del digest — {debug.briefingDate}</h1>
          <p className="page-header__meta">
            Revisione {debug.revision} · modello {debug.llmModel ?? EMPTY} ·{' '}
            {debug.generatedAt ? formatDateTime(debug.generatedAt) : EMPTY} ·{' '}
            <Link to={`/briefing?date=${debug.briefingDate}&revision=${debug.revision}`}>
              torna al digest
            </Link>
          </p>
        </div>
        <Badge tone={debug.status === 'completed' ? 'positive' : 'negative'}>{debug.status}</Badge>
      </header>

      <Card title="Consumo" subtitle="I token dichiarati dal provider per questa generazione.">
        <dl className="trace__fields">
          <div className="trace__field">
            <dt>Prompt</dt>
            <dd>{debug.promptTokens ?? EMPTY}</dd>
          </div>
          <div className="trace__field">
            <dt>Risposta</dt>
            <dd>{debug.completionTokens ?? EMPTY}</dd>
          </div>
          <div className="trace__field">
            <dt>Titoli trattati</dt>
            <dd>{debug.items.length}</dd>
          </div>
        </dl>
      </Card>

      <Card
        title="Titoli trattati"
        subtitle="Nell'ordine in cui il modello li ha restituiti. Un titolo che manca è la prima cosa da guardare."
        flush
      >
        <TableWrap>
          <table className="table table--compact">
            <thead>
              <tr>
                <th>Simbolo</th>
                <th>Nome</th>
                <th>Giudizio</th>
                <th>In portafoglio</th>
              </tr>
            </thead>
            <tbody>
              {debug.items.map((item) => (
                <tr key={`${item.symbol}:${item.name}`}>
                  <td>{item.symbol ?? EMPTY}</td>
                  <td>{item.name}</td>
                  <td>{item.rating ? <Badge tone="accent">{item.rating}</Badge> : EMPTY}</td>
                  <td>{item.inPortfolio ? 'sì' : 'no'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      </Card>

      <RawBlock title="Prompt di sistema (input)" value={debug.systemPrompt} />
      <RawBlock title="Prompt utente (input)" value={debug.userPrompt} />
      <RawBlock title="Risposta grezza (output)" value={debug.rawResponse} />
    </div>
  );
}
